import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const BLANK = {
  name: "",
  type: "bank",
  owner: "",
  current_value: 0,
  notes: "",
};

export const ASSET_TYPE_LABELS = {
  bank: "Bank account",
  investment: "Investment",
  real_estate: "Real estate",
  vehicle: "Vehicle",
  cash: "Cash",
  crypto: "Crypto",
  other: "Other",
};

export default function AssetForm({ open, onOpenChange, onSaved, asset }) {
  const isEdit = Boolean(asset);
  const [form, setForm] = useState(asset ? { ...BLANK, ...asset } : BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(asset ? { ...BLANK, ...asset } : BLANK);
  }, [asset, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        current_value: Number(form.current_value || 0),
      };
      let data;
      if (isEdit) {
        ({ data } = await api.put(`/assets/${asset.id}`, payload));
      } else {
        ({ data } = await api.post(`/assets`, payload));
      }
      toast.success(isEdit ? "Asset updated" : "Asset created");
      onSaved?.(data.asset);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white border-[#E5E3DB] rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="asset-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1D3328]">
            {isEdit ? "Edit asset" : "Add an asset"}
          </DialogTitle>
          <DialogDescription className="text-[#5C635A]">
            Track anything that holds value — bank balances, investments, property, vehicles.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="asset-form">
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Name</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)}
                   placeholder="Eurobank savings" className="rounded-xl border-[#E5E3DB]" data-testid="asset-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="rounded-xl border-[#E5E3DB]" data-testid="asset-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ASSET_TYPE_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Owner</Label>
            <Input value={form.owner} onChange={(e) => set("owner", e.target.value)}
                   placeholder="Elena / Petros / Family"
                   className="rounded-xl border-[#E5E3DB]" data-testid="asset-owner-input" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Current value (€)</Label>
            <Input type="number" step="0.01" min="0" required
                   value={form.current_value}
                   onChange={(e) => set("current_value", e.target.value)}
                   className="rounded-xl border-[#E5E3DB] num" data-testid="asset-value-input" />
            {!isEdit && (
              <p className="text-[11px] text-[#8B9389]">
                You can update this any time. Each update is recorded in the asset&apos;s history.
              </p>
            )}
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                      className="rounded-xl border-[#E5E3DB]" data-testid="asset-notes-input" />
          </div>

          <DialogFooter className="md:col-span-2 mt-2 flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-xl border-[#E5E3DB]" data-testid="asset-form-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving}
                    className="rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white" data-testid="asset-form-submit">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add asset"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
